import { OperationVariables } from '@apollo/client';
import { gqlSimpleQueryAsPromise, SimpleQueryOptions } from 'common/graphql/utils';
import { ResultFormat } from 'common/hooks/table/usePaginatedTable';
import { DocumentNode } from 'graphql';
import { ceil, concat, flatMap, max, times } from 'lodash';

export async function fetchAllPaginatedQueryItems<
    EntityType extends object,
    ResponseItemType extends object = EntityType,
>(
    query: string | DocumentNode,
    {
        queryString,
        variables,
        maxItemsPerQuery,
        responseMapper,
    }: {
        variables?: OperationVariables;
        queryString?: string;
        maxItemsPerQuery?: number;
        responseMapper?: (response: ResultFormat<ResponseItemType>) => ResultFormat<EntityType>;
    } = { responseMapper: (data) => data as object as ResultFormat<EntityType>, maxItemsPerQuery: 500 }
) {
    const queryVariables = { ...(variables || {}), limit: maxItemsPerQuery };

    const peekData = await gqlSimpleQueryAsPromise<ResultFormat<ResponseItemType>>(query, {
        queryString,
        variables: { ...queryVariables, nextToken: 0 },
    });

    const haveAllData = peekData?.totalItems <= maxItemsPerQuery;
    if (haveAllData) {
        return responseMapper(peekData);
    }

    const promises = getSubsequentPaginatedPromises<ResponseItemType>(query, peekData?.totalItems, maxItemsPerQuery, {
        queryOptions: {
            queryString,
            variables: queryVariables,
        },
    });

    const responses = await Promise.all(promises);
    const allItems = concat(
        peekData.items,
        flatMap(responses, (r) => r.items)
    );
    const mergedResponse: ResultFormat<ResponseItemType> = {
        ...peekData,
        items: allItems,
    };

    return responseMapper(mergedResponse);
}

// Get promises for pages 1, 2, 3 (skipping page 0)
export function getSubsequentPaginatedPromises<ResponseItemType>(
    query: string | DocumentNode,
    totalItems: number,
    maxItemsPerQuery: number,
    { queryOptions }: { queryOptions: SimpleQueryOptions } = { queryOptions: { variables: {} } }
) {
    let numPromises = ceil((totalItems ?? 0) / maxItemsPerQuery);
    numPromises = max([numPromises - 1, 0]); // Skip the initial page for which we have items loaded
    const promises = times(numPromises, (index) => {
        const startIndex = 1; // Skip the first loaded batch of items
        const nextToken = (startIndex + index) * maxItemsPerQuery;
        const options = {
            ...queryOptions,
            variables: { ...queryOptions.variables, limit: maxItemsPerQuery, nextToken },
        };

        return gqlSimpleQueryAsPromise<ResultFormat<ResponseItemType>>(query, options);
    });

    return promises;
}
